/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * "Add a follow-up" at call wrap-up: a scheduled task on the caller's record,
 * with an optional reminder for whoever it is assigned to (rules in followUp.js).
 * Plain MUI so the classic panel and the v2 console share it.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { REMINDER_OPTIONS } from "./followUp";

export default function FollowUpFields({ value, onChange, users, currentUserId, disabled, problem }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const userName = (u) => u.full_name || [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username;

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Switch
            size="small"
            checked={Boolean(value.enabled)}
            disabled={disabled}
            onChange={(e) => set({ enabled: e.target.checked })}
            inputProps={{ "aria-label": "Add a follow-up task" }}
          />
        }
        label={<Typography sx={{ fontSize: 14, fontWeight: 600, ml: 1 }}>Add a follow-up task</Typography>}
      />

      {value.enabled ? (
        <>
          <TextField
            size="small"
            label="Follow-up"
            value={value.title}
            disabled={disabled}
            onChange={(e) => set({ title: e.target.value })}
            fullWidth
          />
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.5}>
            <TextField
              size="small"
              type="datetime-local"
              label="Due"
              value={value.due}
              disabled={disabled}
              onChange={(e) => set({ due: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              select
              size="small"
              label="Remind"
              value={value.reminder}
              disabled={disabled}
              onChange={(e) => set({ reminder: e.target.value })}
              fullWidth
            >
              {REMINDER_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {users?.length ? (
            <TextField
              select
              size="small"
              label="Assigned to"
              value={value.assignedTo || currentUserId || ""}
              disabled={disabled}
              onChange={(e) => set({ assignedTo: Number(e.target.value) || null })}
              fullWidth
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.id === currentUserId ? `${userName(u)} (me)` : userName(u)}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {problem ? (
            <Typography role="alert" sx={{ fontSize: 13, color: "#B42318" }}>
              {problem}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: "#5B6B85" }}>
              Saved as a scheduled task on this record when you save the call.
            </Typography>
          )}
        </>
      ) : null}
    </Box>
  );
}

FollowUpFields.propTypes = {
  value: PropTypes.shape({
    enabled: PropTypes.bool,
    title: PropTypes.string,
    due: PropTypes.string,
    reminder: PropTypes.string,
    assignedTo: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(PropTypes.object),
  currentUserId: PropTypes.number,
  disabled: PropTypes.bool,
  problem: PropTypes.string,
};

FollowUpFields.defaultProps = {
  users: [],
  currentUserId: null,
  disabled: false,
  problem: "",
};
